// =====================================================================
// Pure helpers for mutating the chart state jsonb shape.
// =====================================================================
// The state shape (matches supabase chart_drafts.state / chart_snapshots.state):
//
//   { version: 1,
//     teeth: { "18": { findings: [{ finding_code, surfaces, notes? }] } },
//     bridges: [] }
//
// Each tooth holds one entry per finding code. Surface-scoped findings
// carry a non-empty `surfaces` array; whole-tooth findings carry an
// empty `surfaces` array. The Note finding carries `surfaces: []` and
// a non-empty `notes` field.
// =====================================================================

import { getSinglePenExclusiveCodes, isSinglePenCode } from "./singlePenStyles";
import { isBridgeFindingCode } from "./chartFindingBehavior";

export const EMPTY_STATE = { version: 1, teeth: {}, bridges: [] };

export const AUTO_BLEEDING_TRIGGER_CODES = new Set([
  "inflammation_moderate_gi2",
  "inflammation_severe_gi3",
]);

const WHOLE_TOOTH_FINDING_CODES = new Set([
  "bleeding_on_probing",
]);

function preservesWithoutSurfaces(code) {
  return code === "note" || isSinglePenCode(code) || WHOLE_TOOTH_FINDING_CODES.has(code) || isBridgeFindingCode(code);
}

function clone(state) {
  // Shallow-cloning is fine because we always replace the touched tooth
  // entry by value. Saves us depending on structuredClone for older browsers.
  return {
    version: state?.version ?? 1,
    teeth: { ...(state?.teeth ?? {}) },
    bridges: Array.isArray(state?.bridges) ? [...state.bridges] : [],
  };
}

function withTooth(state, fdi, mutator) {
  const next = clone(state);
  const key = String(fdi);
  const cur = next.teeth[key] ?? { findings: [] };
  const newFindings = mutator([...(cur.findings ?? [])]);
  if (newFindings.length === 0) {
    delete next.teeth[key];
  } else {
    next.teeth[key] = { ...cur, findings: newFindings };
  }
  return next;
}

// ---------------------------------------------------------------------
// Surface-scoped finding (caries, restoration, fracture, etc.).
// Adds the surface to the finding's surfaces; merges into an existing
// row of the same code if present. Different findings can coexist on
// the same surface so the Tooth renderer can show stacked split colors.
// ---------------------------------------------------------------------
export function applySurfaceFinding(state, fdi, surface, code) {
  return withTooth(state, fdi, (findings) => {
    const idx = findings.findIndex((f) => f.finding_code === code);
    if (idx === -1) {
      return [...findings, { finding_code: code, surfaces: [surface] }];
    }
    const existing = findings[idx];
    if ((existing.surfaces ?? []).includes(surface)) return findings;
    const merged = { ...existing, surfaces: [...(existing.surfaces ?? []), surface] };
    const out = [...findings];
    out[idx] = merged;
    return out;
  });
}

export function applySurfaceFindingWithAutoFlags(state, fdi, surface, code) {
  const next = applySurfaceFinding(state, fdi, surface, code);
  if (!AUTO_BLEEDING_TRIGGER_CODES.has(code)) return next;
  return applyToothFinding(next, fdi, "bleeding_on_probing", [], {
    auto_flagged_by: "inflammation",
  });
}

// ---------------------------------------------------------------------
// Whole-tooth finding (missing, crown, existing_bridge, perio_pocket, …).
// Replaces any prior whole-tooth finding of the same code; "missing"
// also clears any surface findings (the tooth is gone).
// ---------------------------------------------------------------------
export function applyToothFinding(state, fdi, code, exclusiveCodes = [], extra = {}) {
  return withTooth(state, fdi, (findings) => {
    const exclusiveSet = new Set([
      ...exclusiveCodes,
      ...getSinglePenExclusiveCodes(code),
    ].filter((c) => c !== code));
    const others = findings.filter((f) => f.finding_code !== code && !exclusiveSet.has(f.finding_code));
    return [...others, { finding_code: code, surfaces: [], ...extra }];
  });
}

// ---------------------------------------------------------------------
// Eraser — surface-level
// ---------------------------------------------------------------------
export function eraseSurface(state, fdi, surface) {
  return withTooth(state, fdi, (findings) => {
    return findings.map((f) => {
      if (preservesWithoutSurfaces(f.finding_code)) return f;
      const next = (f.surfaces ?? []).filter((s) => s !== surface);
      return { ...f, surfaces: next };
    }).filter((f) => {
      if (preservesWithoutSurfaces(f.finding_code)) return true;
      return (f.surfaces ?? []).length > 0;
    });
  });
}

// ---------------------------------------------------------------------
// Eraser — whole tooth (clears every finding except the note).
// ---------------------------------------------------------------------
export function clearTooth(state, fdi) {
  return withTooth(state, fdi, (findings) => {
    return findings.filter((f) => f.finding_code === "note");
  });
}

// ---------------------------------------------------------------------
// Remove one charted finding from one tooth. Used by the written
// findings summary so the list and visual chart mutate the same draft
// state instead of maintaining separate copies.
// ---------------------------------------------------------------------
export function removeToothFinding(state, fdi, code) {
  return withTooth(state, fdi, (findings) => {
    const kept = findings.filter((f) => f.finding_code !== code);
    if (!AUTO_BLEEDING_TRIGGER_CODES.has(code)) return kept;
    const stillHasBleedingTrigger = kept.some((f) => AUTO_BLEEDING_TRIGGER_CODES.has(f.finding_code));
    if (stillHasBleedingTrigger) return kept;
    return kept.filter((f) => !(f.finding_code === "bleeding_on_probing" && f.auto_flagged_by === "inflammation"));
  });
}

// ---------------------------------------------------------------------
// Per-tooth note (the orange pill at the bottom).
// ---------------------------------------------------------------------
export function setNote(state, fdi, text) {
  const clean = (text || "").trim();
  return withTooth(state, fdi, (findings) => {
    const others = findings.filter((f) => f.finding_code !== "note");
    if (clean.length === 0) return others;
    return [...others, { finding_code: "note", surfaces: [], notes: clean }];
  });
}

export function getNoteText(state, fdi) {
  const t = state?.teeth?.[String(fdi)];
  if (!t) return "";
  const note = (t.findings ?? []).find((f) => f.finding_code === "note");
  return (note?.notes ?? "").trim();
}
